import { Column } from '@ant-design/charts'
import { Card, Empty, Typography } from 'antd'

import { ChartBody, useChartTheme } from '@/components/charts/chartTheme'

import type { ChargeWindow } from '../useBusinessQueries'
import { balanceFlow, type FlowPoint, periodLabel } from './chargePeriods'
import type { AdminCharges } from './chargeSummary'

function BalanceFlowChart({
  points,
  window,
  range,
}: {
  points: FlowPoint[]
  window: ChargeWindow
  range: { from: string; to: string }
}) {
  const chart = useChartTheme()
  const data = points.map((point) => ({
    ...point,
    period: periodLabel(point.key, window, range),
  }))
  return (
    <Column
      data={data}
      xField="period"
      yField="value"
      colorField="series"
      group
      height={260}
      axis={{
        x: { ...chart.axis.x, labelAutoHide: true },
        y: { ...chart.axis.y, labelFormatter: (v: number) => `$${v}` },
      }}
      legend={chart.legend}
      tooltip={{
        items: [{ field: 'value', valueFormatter: (v: number) => `$${v}` }],
      }}
      style={{ maxWidth: 24 }}
    />
  )
}

/** Money in and out of balances per period. */
export function BalanceFlowCard({
  charges,
  credited,
  loading,
  step,
}: {
  charges?: AdminCharges
  credited?: { day: string; value: number }[]
  loading: boolean
  step: ChargeWindow
}) {
  const data = charges && credited ? { charges, credited } : undefined
  return (
    <Card title="Credited and charged" style={{ marginTop: 16 }}>
      <ChartBody
        data={data}
        loading={loading}
        errorMessage="Failed to load credited or charged amounts"
      >
        {({ charges, credited }) => {
          const points = balanceFlow(charges, credited, step)
          return points.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Nothing credited or charged in this period"
            />
          ) : (
            <BalanceFlowChart points={points} window={step} range={charges} />
          )
        }}
      </ChartBody>
      <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
        Credited: real-money top-ups of all accounts, whatever the tab, by the
        day they reached the ledger. Charged: usage of the selected accounts by
        the time it was sent to OpenMeter; it also spends promo and manual
        credits. The first and last periods are partial: the range ends now.
      </Typography.Paragraph>
    </Card>
  )
}
